import { batch } from 'react-redux';
import PHONE_CONSTRAINT_NAMES from '../../Data/phoneConstraintNames';
import POSTCODE_CONSTRAINT_NAMES from '../../Data/postcodeConstraintNames';
import { validatePhoneSaga } from '../../Lib/redux/sagas/ValidatePhone/validatePhone';
import { validatePostcodeSaga } from '../../Lib/redux/sagas/ValidatePostcode/validatePostcode';
import {
  setComponentValidationStatus,
  updateMaskedValue,
  updateValue,
} from '../../Lib/redux/slices/components';
import { incrementMetric } from '../../Services/Datadog';
import { mask } from '../../Utils/masking';
import { handleAnswerQuestionSideEffectsSaga } from '../actionCreators';

const getDecryptedData = (data, formId) => {
  let decryptedDataJson;
  try {
    decryptedDataJson = JSON.parse(data);
  } catch (error) {
    incrementMetric({
      name: 'formError',
      tags: {
        error_thrown: 'Failed to parse / decrypt CRM Autofill Data',
        form_id: formId,
      },
    });
  }

  return decryptedDataJson;
};

const updateCrmData = ({
  decryptedData,
  mappedQuestionIdToComponentId,
  urlAliasComponentIdMaps,
  dispatch,
  countryCode,
  iFrameId,
  formId,
  components,
}) => {
  const decryptedDataJson = getDecryptedData(decryptedData, formId);

  if (!decryptedDataJson) {
    return;
  }

  const updateValueAndValidate = (componentId, value, crmDataKey) => {
    batch(() => {
      dispatch(updateValue({ componentId, value }));
      if (
        POSTCODE_CONSTRAINT_NAMES.includes(crmDataKey) &&
        components[`component_${componentId}`]?.validation?.constraints
          ?.postcode
      ) {
        const maskPattern =
          components[`component_${componentId}`]?.input?.maskPattern;

        if (maskPattern) {
          dispatch(
            updateMaskedValue({
              componentId,
              maskedValue: mask({ value, maskPattern }),
            })
          );
          dispatch(
            validatePostcodeSaga({
              countryCode,
              componentId,
              value: mask({ value, maskPattern }),
              iFrameId,
            })
          );
        } else {
          dispatch(
            validatePostcodeSaga({
              countryCode,
              componentId,
              value,
              iFrameId,
            })
          );
        }
      } else if (PHONE_CONSTRAINT_NAMES.includes(crmDataKey)) {
        const constraints =
          components[`component_${componentId}`]?.validation?.constraints;
        dispatch(
          validatePhoneSaga({
            country: countryCode,
            number: value,
            componentId,
            iFrameId,
            constraints,
          })
        );
      } else {
        dispatch(
          setComponentValidationStatus({
            countryCode,
            componentId,
            iFrameId,
            formId,
          })
        );
        dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
      }
    });
  };

  // If decrypted data has a last name but the form doesn't have a last name question,
  // concatenate first name and last name and update the first name question with the full name.
  const useConcatenatedFirstAndLastName =
    mappedQuestionIdToComponentId?.last_name === undefined &&
    decryptedDataJson?.last_name !== undefined;

  if (useConcatenatedFirstAndLastName) {
    decryptedDataJson.first_name = `${decryptedDataJson.first_name} ${decryptedDataJson.last_name}`;
  }

  Object.keys(decryptedDataJson).forEach((crmDataKey) => {
    const componentId = urlAliasComponentIdMaps[crmDataKey];
    const value = decryptedDataJson[crmDataKey];
    const shouldUpdate =
      componentId &&
      value &&
      !(
        useConcatenatedFirstAndLastName &&
        (crmDataKey === 'first_name' || crmDataKey === 'last_name')
      );

    if (shouldUpdate) {
      updateValueAndValidate(componentId, value, crmDataKey);
    }
  });

  if (useConcatenatedFirstAndLastName) {
    const componentId = urlAliasComponentIdMaps.first_name;
    if (!componentId) {
      return;
    }
    updateValueAndValidate(
      componentId,
      decryptedDataJson.first_name,
      'first_name'
    );
  }
};

export default updateCrmData;
